import React, { useEffect, useState } from "react";
import { api, extractError, fetchAllListResults } from "../lib/api";
import { useAuth, hasRole, isAdmin } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import {
  buildPurchaseContractPayload,
  contractToForm,
  isArrivalPhase,
  isOnArrivalDirectPurchase,
  isPreArrivalPhase,
  labelFor,
  partyKindLabel,
  partyLabel,
  paymentLabel,
  phaseLabel,
  purchaseTypesForPhase,
} from "../lib/purchaseContractOptions";
import { Pill } from "./Ui";
import PurchaseContractSampleLink from "./PurchaseContractSampleLink";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Lock, Trash2 } from "lucide-react";
import PurchaseContractFormFields from "./PurchaseContractFormFields";
import PurchaseContractDocumentActions from "./PurchaseContractDocumentActions";
import PurchaseContractFulfillmentPanel from "./PurchaseContractFulfillmentPanel";

export default function PurchaseContractDetailDialog({
  contractId,
  open,
  onOpenChange,
  onSaved,
  onDeleted,
  partyPickers = null,
}) {
  const { user } = useAuth();
  const canEdit = isAdmin(user) || hasRole(user, ["manager", "purchaser"]);
  const canSoftDelete = user?.role === "manager";
  const canHardDelete = isAdmin(user);
  const readOnly = !canEdit;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contract, setContract] = useState(null);
  const [form, setForm] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [linkableLabs, setLinkableLabs] = useState([]);
  const [linkableGatePasses, setLinkableGatePasses] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);

  const isArrival = form && isArrivalPhase(form.contract_phase);
  const isPreArrival = form && isPreArrivalPhase(form.contract_phase);
  const onArrivalDirect = form && isOnArrivalDirectPurchase(form);
  const isStock = form?.purchase_type === "STOCK_PURCHASE";
  const sampleAnalysed = form?.stock_sample_analysed === "yes";

  useEffect(() => {
    if (!open || !contractId) {
      setContract(null);
      setForm(null);
      return;
    }
    setLoading(true);
    api
      .get(`/purchase-contracts/${contractId}`)
      .then((r) => {
        setContract(r.data);
        setForm(contractToForm(r.data));
      })
      .catch((err) => {
        toast.error(extractError(err));
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, contractId, onOpenChange]);

  useEffect(() => {
    if (!open || readOnly) return;
    if (partyPickers) {
      setBrokers(partyPickers.brokers ?? []);
      setFarmers(partyPickers.farmers ?? []);
      setSuppliers(partyPickers.suppliers ?? []);
      return;
    }
    Promise.all([
      fetchAllListResults("/parties", { kind: "BROKER" }),
      fetchAllListResults("/parties", { kind: "FARMER" }),
      fetchAllListResults("/parties", { kind: "SUPPLIER" }),
    ])
      .then(([b, f, s]) => {
        setBrokers(b);
        setFarmers(f);
        setSuppliers(s);
      })
      .catch(() => {
        setBrokers([]);
        setFarmers([]);
        setSuppliers([]);
      });
  }, [open, readOnly, partyPickers]);

  useEffect(() => {
    if (!open || readOnly || !isArrival) {
      setLinkableGatePasses([]);
      return;
    }
    api
      .get("/purchase-contracts/linkable-gate-passes", { params: { exclude_contract_id: contractId } })
      .then((r) => {
        const linked = contract?.gate_pass_id
          ? [
              {
                id: String(contract.gate_pass_id),
                code: contract.gate_pass_code || "Linked",
                vehicle_number: "",
                party_name: null,
              },
            ]
          : [];
        const ids = new Set(r.data.map((x) => x.id));
        setLinkableGatePasses([...linked.filter((x) => !ids.has(x.id)), ...r.data]);
      })
      .catch(() => setLinkableGatePasses([]));
  }, [open, readOnly, isArrival, contractId, contract]);

  useEffect(() => {
    if (!open || readOnly || !isPreArrival || !isStock || !sampleAnalysed) {
      setLinkableLabs([]);
      return;
    }
    api
      .get("/purchase-contracts/linkable-stack-lab-reports", {
        params: contractId ? { purchase_contract_id: contractId } : {},
      })
      .then((r) => {
        const linked = contract?.stack_sample_lab_report_id
          ? [
              {
                id: String(contract.stack_sample_lab_report_id),
                intake_code: contract.linked_lab_report?.intake_code || "Linked",
                verdict: contract.linked_lab_report?.verdict || "",
                moisture_pct: contract.linked_lab_report?.moisture_pct,
              },
            ]
          : [];
        const ids = new Set(r.data.map((x) => x.id));
        setLinkableLabs([...linked.filter((x) => !ids.has(x.id)), ...r.data]);
      })
      .catch(() => setLinkableLabs([]));
  }, [open, readOnly, isPreArrival, isStock, sampleAnalysed, contract]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (isArrival && !form.gate_pass_id) {
      toast.error("Select a gate pass for arrival contracts");
      return;
    }
    if (onArrivalDirect && !form.quality) {
      toast.error("Select quality / item for on-arrival direct purchase");
      return;
    }
    if (!onArrivalDirect && !form.quantity) {
      toast.error("Quantity is required");
      return;
    }
    if (isPreArrival && isStock && !form.stock_sample_analysed) {
      toast.error("Please indicate if the stock sample is already analysed");
      return;
    }
    if (isPreArrival && isStock && sampleAnalysed && !form.stack_sample_lab_report_id) {
      toast.error("Select a stack sample lab report to link");
      return;
    }
    try {
      setSubmitting(true);
      await api.patch(`/purchase-contracts/${contractId}`, buildPurchaseContractPayload(form));
      toast.success("Contract updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const canCloseContract =
    canEdit && contract?.status === "ACTIVE" && !contract?.has_open_purchase;

  const handleClose = async () => {
    try {
      setSubmitting(true);
      await api.post(`/purchase-contracts/${contractId}/close`);
      toast.success("Contract closed");
      setConfirmClose(false);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      await api.delete(`/purchase-contracts/${contractId}`);
      toast.success("Contract removed");
      setConfirmDelete(false);
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openHardDelete = async () => {
    setPurgePreview(null);
    setConfirmHardDelete(true);
    setPurgeLoading(true);
    try {
      const res = await api.get(`/purchase-contracts/${contractId}/purge-preview`);
      setPurgePreview(res.data);
    } catch (err) {
      toast.error(extractError(err));
      setConfirmHardDelete(false);
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleHardDelete = async () => {
    try {
      setSubmitting(true);
      const res = await api.post(`/purchase-contracts/${contractId}/purge`, {
        force: Boolean(purgePreview?.requires_force),
      });
      toast.success(
        res.data?.purchases_fully_removed
          ? `Contract and ${res.data.purchases_fully_removed} linked purchase(s) permanently deleted`
          : "Contract permanently deleted",
      );
      setConfirmHardDelete(false);
      setPurgePreview(null);
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="purchase-contract-detail-dialog">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pr-8">
              <div>
                <DialogTitle className="font-mono">{contract?.contract_number || "Contract"}</DialogTitle>
                <DialogDescription>
                  {readOnly ? "View contract details" : "Edit contract details"}
                  {contract && (
                    <span className="block mt-1 text-xs">
                      {phaseLabel(contract.contract_phase)}
                      {contract.created_by && ` · Created by ${contract.created_by}`}
                      {contract.updated_by && ` · Updated by ${contract.updated_by}`}
                    </span>
                  )}
                </DialogDescription>
              </div>
              {contractId && contract && (
                <PurchaseContractDocumentActions
                  contractId={contractId}
                  contractNumber={contract.contract_number}
                />
              )}
            </div>
          </DialogHeader>

          {loading || !form ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : readOnly ? (
            <>
              <ReadOnlyBody contract={contract} />
              <PurchaseContractFulfillmentPanel contractId={contractId} />
            </>
          ) : (
            <>
              <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PurchaseContractFormFields
                form={form}
                setForm={setForm}
                contractNumber={contract.contract_number}
                brokers={brokers}
                farmers={farmers}
                suppliers={suppliers}
                linkableLabs={linkableLabs}
                linkableGatePasses={linkableGatePasses}
                showPhaseSelector={false}
              />
              <DialogFooter className="md:col-span-3 sm:justify-between gap-2 pt-2">
                <div className="flex flex-wrap gap-2">
                  {canCloseContract && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setConfirmClose(true)}
                      disabled={submitting}
                      data-testid="purchase-contract-close-btn"
                    >
                      <Lock className="h-4 w-4 mr-1" /> Close contract
                    </Button>
                  )}
                  {canEdit && contract?.status === "ACTIVE" && contract?.has_open_purchase && (
                    <p className="text-xs text-muted-foreground self-center">
                      Close unavailable while trucks are in progress
                    </p>
                  )}
                  {canSoftDelete && (
                    <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)} disabled={submitting}>
                      <Trash2 className="h-4 w-4 mr-1" /> Archive
                    </Button>
                  )}
                  {canHardDelete && (
                    <Button type="button" variant="destructive" onClick={openHardDelete} disabled={submitting}>
                      <Trash2 className="h-4 w-4 mr-1" /> Delete permanently
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                    {submitting ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
              <PurchaseContractFulfillmentPanel contractId={contractId} />
            </>
          )}

          {readOnly && !loading && contract && (
            <DialogFooter className="sm:justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                {canHardDelete && (
                  <Button type="button" variant="destructive" onClick={openHardDelete} disabled={submitting}>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete permanently
                  </Button>
                )}
              </div>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close contract?</AlertDialogTitle>
            <AlertDialogDescription>
              {contract?.balance_qtl != null && contract.balance_qtl > 0 ? (
                <>
                  This contract still has <strong>{contract.balance_qtl} qtl</strong> balance remaining.
                  Closing will prevent new trucks from using it.
                </>
              ) : (
                <>
                  Fulfillment is complete for contract <strong>{contract?.contract_number}</strong>.
                  It will be marked closed and hidden from weighbridge selection.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClose} className="bg-bansal-sage hover:bg-bansal-sageDark">
              Close contract
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive contract?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive contract <strong>{contract?.contract_number}</strong>. It will no longer appear in lists but remains in the database for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmHardDelete}
        onOpenChange={(open) => {
          setConfirmHardDelete(open);
          if (!open) setPurgePreview(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete contract?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {purgeLoading ? (
                  <p>Loading linked records…</p>
                ) : purgePreview ? (
                  <>
                    <p>
                      This will <strong>permanently remove</strong> contract{" "}
                      <strong>{purgePreview.contract_number}</strong> and cannot be undone.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      {purgePreview.purchases_fully_removed > 0 && (
                        <li>
                          {purgePreview.purchases_fully_removed} truck purchase(s) and related lab, unloading, and voucher records
                          {purgePreview.purchase_codes_fully_removed?.length > 0 && (
                            <> ({purgePreview.purchase_codes_fully_removed.join(", ")})</>
                          )}
                        </li>
                      )}
                      {purgePreview.purchases_partially_updated > 0 && (
                        <li>
                          Unlink from {purgePreview.purchases_partially_updated} multi-contract truck(s) (
                          {purgePreview.purchase_codes_partially_updated?.join(", ")})
                        </li>
                      )}
                      {purgePreview.vouchers > 0 && <li>{purgePreview.vouchers} voucher(s)</li>}
                      {purgePreview.stack_sample_intakes_removed > 0 && (
                        <li>{purgePreview.stack_sample_intakes_removed} stack sample intake(s)</li>
                      )}
                      {purgePreview.purchases_fully_removed === 0 &&
                        purgePreview.purchases_partially_updated === 0 &&
                        purgePreview.vouchers === 0 &&
                        purgePreview.stack_sample_intakes_removed === 0 && (
                          <li>No linked truck or voucher records — only the contract row will be removed.</li>
                        )}
                    </ul>
                    {purgePreview.requires_force && (
                      <p className="text-destructive font-medium">
                        Trucks are still in progress for this contract. Deleting will remove in-flight purchases.
                      </p>
                    )}
                  </>
                ) : (
                  <p>Unable to load purge preview.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDelete}
              disabled={submitting || purgeLoading || !purgePreview}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {submitting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ReadOnlyBody({ contract: c }) {
  const { optionsFor } = useLookups();
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
      <Dt label="Phase">{phaseLabel(c.contract_phase)}</Dt>
      <Dt label="Date">{c.contract_date}</Dt>
      <Dt label="Type">{labelFor(purchaseTypesForPhase(c.contract_phase), c.purchase_type)}</Dt>
      <Dt label="Party type">{partyKindLabel(c.party_kind) || "—"}</Dt>
      <Dt label="Party">{partyLabel(c)}</Dt>
      <Dt label="Gate pass">{c.gate_pass_code || "—"}</Dt>
      <Dt label="Quality / item">{c.quality}</Dt>
      <Dt label="Quantity">
        {c.purchase_type === "ON_ARRIVAL_DIRECT_PURCHASE" ? "—" : `${c.quantity} qtl`}
      </Dt>
      <Dt label="Rate">₹{c.price}/qtl</Dt>
      <Dt label="Payment">{paymentLabel(c)}</Dt>
      <Dt label="Bardana">{labelFor(optionsFor("bardana"), c.bardana)}</Dt>
      {c.purchase_type !== "MANDI_PURCHASE" && (
        <Dt label={c.purchase_type === "FOR_FACTORY" ? "Per qtl weight deduction" : "Bardana deduction"}>
          {labelFor(optionsFor("bardana_deduction"), c.bardana_deduction)}
        </Dt>
      )}
      {c.purchase_type === "FOR_FACTORY" && (
        <Dt label="Bardana return">{c.bardana_return === false ? "No" : "Yes — per bag, from the supplier chart"}</Dt>
      )}
      {c.purchase_type === "MANDI_PURCHASE" && c.weight_basis && (
        <Dt label="Weight basis">{String(c.weight_basis).replace(/_/g, " ")}</Dt>
      )}
      <Dt label="Delivery">{labelFor(optionsFor("delivery_condition"), c.delivery_condition)}</Dt>
      <Dt label="Freight rate">{c.freight_rate_per_qtl != null ? `₹${c.freight_rate_per_qtl}/qtl` : "—"}</Dt>
      <Dt label="CD">{c.cd_applicable ? `${c.cd_percent}%` : "No"}</Dt>
      <Dt label="Broker">
        {c.broker_applicable ? `${c.broker_party_name || c.broker_name} · ₹${c.brokerage}` : "—"}
      </Dt>
      <Dt label="Status">
        <Pill tone={c.status === "CLOSED" ? "muted" : "sage"}>{c.status}</Pill>
      </Dt>
      {c.contract_phase === "PRE_ARRIVAL" && c.purchase_type === "STOCK_PURCHASE" && (
        <Dt label="Stock / Lab" className="sm:col-span-2">
          <PurchaseContractSampleLink contract={c} />
        </Dt>
      )}
      {c.quality_conditions && (
        <Dt label="Quality conditions" className="sm:col-span-2">{c.quality_conditions}</Dt>
      )}
    </dl>
  );
}

function Dt({ label, children, className }) {
  return (
    <div className={className}>
      <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
